import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Camera } from 'lucide-react'
import { cn, formatDate } from '@shared/utils'
import { accountLabel, useCurrentUser } from '@modules/auth'
import { mediaService } from '@modules/media'
import { useProfile, useUpdateProfile } from '../hooks/useProfile'
import { toast } from 'sonner'

const profileSchema = z.object({
  firstName: z.string().max(100).optional().or(z.literal('')),
  lastName: z.string().max(100).optional().or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
})

type ProfileFormValues = z.infer<typeof profileSchema>

// ── Avatar Section ────────────────────────────────────────────────────────────
function AvatarSection({
  profile,
  profileLoading,
  initial,
}: {
  profile: ReturnType<typeof useProfile>['data']
  profileLoading: boolean
  initial: string
}) {
  const { mutateAsync: updateProfile } = useUpdateProfile()
  const [uploading, setUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const { url } = await mediaService.uploadAvatar(file)
      await updateProfile({ avatarUrl: url })
    } catch {
      toast.error('Tải ảnh lên thất bại')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        {profile?.avatarUrl ? (
          <img
            src={profile.avatarUrl}
            alt="avatar"
            className="h-20 w-20 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-500 text-2xl font-semibold text-white">
            {profileLoading ? '…' : initial}
          </div>
        )}
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:bg-secondary disabled:opacity-50"
          title="Đổi ảnh đại diện"
        >
          <Camera className="size-3.5" />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarChange}
        />
      </div>
      {uploading && <p className="text-xs text-muted-foreground">Đang tải ảnh...</p>}
    </div>
  )
}

// ── Thông tin cá nhân ──
/** Cài đặt › Tài khoản — thông tin cá nhân, ảnh đại diện */
export function ProfileInfoPanel() {
  const { user } = useCurrentUser()
  const { data: profile, isLoading: profileLoading } = useProfile()
  const { mutateAsync: updateProfile, isPending: updatingProfile } = useUpdateProfile()

  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ')
  const initial = (fullName || profile?.email || user?.email || 'A')[0].toUpperCase()

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    values: {
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      phone: profile?.phone ?? '',
    },
  })

  const handleSubmit = async (values: ProfileFormValues) => {
    try {
      await updateProfile({
        firstName: values.firstName || null,
        lastName: values.lastName || null,
        phone: values.phone || null,
      })
    } catch {
      // error handled by mutation onError (toast)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Left: Account summary */}
      <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold text-foreground">Thông tin cơ bản</h2>

        <AvatarSection profile={profile} profileLoading={profileLoading} initial={initial} />

        <div className="mt-4 space-y-2 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="truncate font-medium text-foreground">{profile?.email ?? user?.email}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Vai trò</p>
            {/* vai trò chính + công ty (01/10) — `profile.role` chỉ là loại tài khoản ('admin'), không phải chức vụ */}
            <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-foreground">
              {accountLabel(user)}
            </span>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Trạng thái</p>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-state-ok">
              <span className="h-1.5 w-1.5 rounded-full bg-state-ok" />
              Đang hoạt động
            </span>
          </div>
          {profile?.createdAt && (
            <div>
              <p className="text-xs text-muted-foreground">Thành viên từ</p>
              <p className="text-foreground">{formatDate(profile.createdAt)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Right: Edit form */}
      <div className="rounded-lg border border-border bg-card p-6 shadow-sm lg:col-span-2">
        <h2 className="mb-4 text-sm font-semibold text-foreground">Chỉnh sửa thông tin</h2>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="firstName">
                Họ
              </label>
              <input
                id="firstName"
                type="text"
                placeholder="Nguyễn"
                className={cn(
                  'h-8 w-full rounded-lg border border-border bg-background px-2 text-sm text-foreground',
                  'placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                  form.formState.errors.firstName && 'border-destructive',
                )}
                {...form.register('firstName')}
              />
              {form.formState.errors.firstName && (
                <p className="text-xs text-state-critical">{form.formState.errors.firstName.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="lastName">
                Tên
              </label>
              <input
                id="lastName"
                type="text"
                placeholder="Văn A"
                className={cn(
                  'h-8 w-full rounded-lg border border-border bg-background px-2 text-sm text-foreground',
                  'placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                  form.formState.errors.lastName && 'border-destructive',
                )}
                {...form.register('lastName')}
              />
              {form.formState.errors.lastName && (
                <p className="text-xs text-state-critical">{form.formState.errors.lastName.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="phone">
                Số điện thoại
              </label>
              <input
                id="phone"
                type="tel"
                placeholder="0901 234 567"
                className={cn(
                  'h-8 w-full rounded-lg border border-border bg-background px-2 text-sm text-foreground',
                  'placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                  form.formState.errors.phone && 'border-destructive',
                )}
                {...form.register('phone')}
              />
              {form.formState.errors.phone && (
                <p className="text-xs text-state-critical">{form.formState.errors.phone.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={updatingProfile}
              className="h-8 rounded-lg bg-brand-500 px-6 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {updatingProfile ? 'Đang lưu...' : 'Lưu thay đổi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
